import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { OBSIDIAN } from '../../constants/obsidianDashboardTheme';
import {
    formatSyncUploadDisplayDate,
    getSyncUploadApprovalDeadline,
    SYNC_UPLOAD_APPROVAL_LEAD_DAYS,
} from '../../utils/syncUploadCampaign';

const ctaSx = {
    alignSelf: 'flex-start',
    px: 2.5,
    py: 1.1,
    borderRadius: '10px',
    textTransform: 'none' as const,
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
    fontWeight: 700,
    fontSize: 14,
    letterSpacing: '0.01em',
    background: `linear-gradient(90deg, ${OBSIDIAN.primary} 0%, ${OBSIDIAN.primaryDim} 100%)`,
    color: OBSIDIAN.onPrimaryFixed,
    boxShadow: '0 4px 18px rgba(255, 201, 101, 0.28)',
    '&:hover': {
        background: `linear-gradient(90deg, #ffd080 0%, ${OBSIDIAN.primaryDim} 100%)`,
    },
};

export interface CampaignSyncUploadNoticeProps {
    syncPublishDate: string;
    /** null = unknown / not signed in; false = missing OAuth link; true = auto-linked */
    isAutoLinked?: boolean | null;
    onConnectAccounts?: () => void;
    onSignIn?: () => void;
    isSignedIn?: boolean;
    sx?: object;
}

export function CampaignSyncUploadNotice({
    syncPublishDate,
    isAutoLinked,
    onConnectAccounts,
    onSignIn,
    isSignedIn,
    sx,
}: CampaignSyncUploadNoticeProps) {
    const publishLabel = formatSyncUploadDisplayDate(syncPublishDate);
    const approvalDeadline = getSyncUploadApprovalDeadline(syncPublishDate);
    const approvalLabel = formatSyncUploadDisplayDate(approvalDeadline);
    const needsLink = isAutoLinked === false;
    const needsSignIn = !isSignedIn && isAutoLinked !== true;
    const showCta = (needsSignIn && onSignIn) || (needsLink && isSignedIn && onConnectAccounts);

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '14px',
                bgcolor: OBSIDIAN.surfaceContainerHigh,
                border: `1px solid ${OBSIDIAN.surfaceContainerHighest}`,
                boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
                ...sx,
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: `linear-gradient(180deg, ${OBSIDIAN.primary} 0%, ${OBSIDIAN.primaryDim} 100%)`,
                }}
            />
            <Box sx={{ pl: 2.75, pr: 2.5, py: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: 'rgba(255,201,101,0.12)',
                            border: '1px solid rgba(255,201,101,0.28)',
                            flexShrink: 0,
                        }}>
                        <CalendarTodayOutlined sx={{ fontSize: 18, color: OBSIDIAN.primary }} />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: OBSIDIAN.primary,
                                lineHeight: 1.2,
                            }}>
                            Coordinated publish
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 800,
                                fontSize: { xs: 17, sm: 18 },
                                color: OBSIDIAN.onSurface,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.25,
                                mt: 0.25,
                            }}>
                            Sync publish campaign
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 14,
                        color: 'rgba(255,255,255,0.88)',
                        lineHeight: 1.6,
                        mb: 1.25,
                    }}>
                    All creators publish on{' '}
                    <Box component="span" sx={{ color: OBSIDIAN.onSurface, fontWeight: 700 }}>
                        {publishLabel}
                    </Box>
                    .
                </Typography>

                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 14,
                        color: 'rgba(255,255,255,0.88)',
                        lineHeight: 1.6,
                        mb: 1.5,
                    }}>
                    Once accepted, create your content and get brand approval by{' '}
                    <Box component="span" sx={{ color: OBSIDIAN.onSurface, fontWeight: 700 }}>
                        {approvalLabel || `${SYNC_UPLOAD_APPROVAL_LEAD_DAYS} days before publish`}
                    </Box>{' '}
                    ({SYNC_UPLOAD_APPROVAL_LEAD_DAYS} days before the sync date). Late or unapproved content may miss
                    the coordinated publish.
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.25,
                        p: 1.5,
                        mb: 1.25,
                        borderRadius: '10px',
                        bgcolor: OBSIDIAN.surfaceContainerHighest,
                        border: '1px solid rgba(255,201,101,0.16)',
                    }}>
                    <LinkOutlined sx={{ fontSize: 18, color: OBSIDIAN.primary, mt: 0.2, flexShrink: 0 }} />
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 13,
                            color: 'rgba(255,255,255,0.85)',
                            lineHeight: 1.55,
                        }}>
                        Your social accounts must be{' '}
                        <Box component="span" sx={{ color: OBSIDIAN.primary, fontWeight: 700 }}>
                            auto-linked with OAuth
                        </Box>{' '}
                        so publishing can be scheduled on the sync date. Manual usernames are not enough for this
                        campaign.
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 13,
                        color: OBSIDIAN.onSurfaceVariant,
                        lineHeight: 1.5,
                        mb: showCta ? 2 : 0,
                    }}>
                    Payout is issued approximately 14 days after your content is published.
                </Typography>

                {needsSignIn && onSignIn ? (
                    <Button size="medium" variant="contained" onClick={onSignIn} sx={ctaSx}>
                        Sign in to connect accounts
                    </Button>
                ) : null}

                {needsLink && isSignedIn && onConnectAccounts ? (
                    <Button size="medium" variant="contained" onClick={onConnectAccounts} sx={ctaSx}>
                        Auto-link accounts
                    </Button>
                ) : null}
            </Box>
        </Box>
    );
}
